import React, { useState, useEffect } from "react";
import { Table, Modal, Form, Input, Button, Select, message, Drawer, Checkbox, Space, notification, Tag, Statistic, Row, Col, Card, Badge } from "antd";
import { JobsColumns, PREDEFINED_SCREENING_QUESTIONS } from "../helper/misc";
import { IJob } from "../helper/interfaces";
import { endpoints } from "src/api/endpoints";
import { createJob, handleSendInvite, axiosInstant } from 'src/api/api';
import { useUser } from 'src/context';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { MinusCircleOutlined, PlusOutlined, MessageOutlined } from "@ant-design/icons";
import { Link } from 'react-router-dom';
import ScreeningCriteriaModal from 'src/Components/RecruitersPanel/ScreeningCriteria/ScreeningCriteriaModal';
// import your jobs data and IJob type

const { Option } = Select;

type JobsPageProps = {
  jobs: IJob[];
  onInvite: (job: IJob) => void;
  onAddJob: (job: IJob) => void;
};

type Invitation = {
  companyName?: string;
  positionTitle?: string;
  // ...add other fields as needed
};

function renderJobDescription(text: string) {
  if (!text) return <span>No job description</span>;
  const lines = text.split(/\n/);
  const elements = [];
  let bullets: string[] = [];

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (/^[-•*]\s+/.test(trimmed)) {
      bullets.push(trimmed.replace(/^[-•*]\s+/, ""));
    } else {
      if (bullets.length) {
        elements.push(
          <ul key={`ul-${idx}`}>
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        );
        bullets = [];
      }
      if (trimmed) {
        elements.push(<p key={`p-${idx}`}>{trimmed}</p>);
      }
    }
  });
  // Add any remaining bullets
  if (bullets.length) {
    elements.push(
      <ul key={`ul-last`}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
  }
  return elements;
}

const JobsPage: React.FC<Omit<JobsPageProps, 'jobs'>> = ({ onInvite, onAddJob }) => {
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
  const [inviteForm] = Form.useForm();
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerJob, setDrawerJob] = useState<IJob | null>(null);
  const [invitesVisible, setInvitesVisible] = useState(false);
  const [invites, setInvites] = useState([]);
  const [invitesJob, setInvitesJob] = useState<IJob | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [selectedInvite, setSelectedInvite] = useState<any | null>(null);
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [currentPage, setCurrentPage] = useState(Number(params.get('page')) || 1);
  const [jobApplicationsCount, setJobApplicationsCount] = useState<{ [jobId: string]: number }>({});
  const [inviteStats, setInviteStats] = useState<{ sent: number, accepted: number, declined: number } | null>(null);
  const [invitesStats, setInvitesStats] = useState<{ sent: number, accepted: number, declined: number } | null>(null);
  const [genLoading, setGenLoading] = useState(false);
  const [hasInviteUpdates, setHasInviteUpdates] = useState(false);
  const [unviewedApplicationsByJob, setUnviewedApplicationsByJob] = useState<{ [jobId: string]: boolean }>({});
  const [jobChatCounts, setJobChatCounts] = useState<{[jobId: string]: number}>({});
  const [screeningCriteriaVisible, setScreeningCriteriaVisible] = useState(false);
  const [selectedJobForScreening, setSelectedJobForScreening] = useState<IJob | null>(null);
  const [isSubmittingJob, setIsSubmittingJob] = useState(false);
  const { jobId } = useParams();

  const fetchJobChatCounts = async () => {
    try {
      if (!user?._id) return;
      const response = await axiosInstant.get(`/notifications/my/job-chat-counts`);
      setJobChatCounts(response.data || {});
    } catch (error) {
      console.error('Error fetching job chat counts:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axiosInstant.get('/jobs/recruiter/my-jobs');
      const sortedJobs = response.data.sort(
        (a: IJob, b: IJob) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setJobs(sortedJobs);
      // Fetch application counts for each job
      const counts: { [jobId: string]: number } = {};
      const newUnviewed: { [key: string]: boolean } = {};
      await Promise.all(
        sortedJobs.map(async (job: IJob) => {
          try {
            const res = await axiosInstant.get(`/job-applications/applications/by-job/${job._id}`);
            console.log('Applications for job', job._id, res.data);
            counts[job._id] = Array.isArray(res.data) ? res.data.length : 0;
            newUnviewed[job._id] = res.data.some((app: any) => !app.viewedByRecruiter);
          } catch {
            counts[job._id] = 0;
            newUnviewed[job._id] = false;
          }
        })
      );
      setJobApplicationsCount(counts);
      setUnviewedApplicationsByJob(newUnviewed);
    } catch (error) {
      message.error("Failed to fetch jobs");
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchJobChatCounts();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = Number(params.get('page')) || 1;
    setCurrentPage(page);
  }, [location.search]);

  useEffect(() => {
    if (jobId) {
      axiosInstant.patch(`/job-applications/mark-viewed/${jobId}`)
        .then(() => {
          // Optionally, update parent state to remove the badge
          if (setUnviewedApplicationsByJob) {
            setUnviewedApplicationsByJob((prev: any) => ({
              ...prev,
              [jobId]: false
            }));
          }
        });
    }
  }, [jobId]);

  useEffect(() => {
    // Only refetch jobs if we're on the jobs list route
    if (location.pathname === '/recruiter/jobs') {
      fetchJobs();
    }
  }, [location.pathname]);

  const handleAddJob = async () => {
    if (isSubmittingJob) return; // Prevent double submission
    
    try {
      setIsSubmittingJob(true);
      const values = await form.validateFields();

      const managers = (values.managers || []).map((m: { name: string; email: string }) => ({
        name: m.name,
        email: m.email,
      }));

      const result = await createJob({
        title: values.positionTitle,
        description: values.jobDescription,
        companyName: values.companyName,
        salary: values.salary,
        workType: values.workType,
        location: values.location,
        positionTitle: values.positionTitle,
        jobDescription: values.jobDescription,
        predefinedQuestions: values.predefinedQuestions,
        customQuestions: values.customQuestions,
        managers,
      });
      setAddModalVisible(false);
      form.resetFields();
      
      // Show matching results notification
      if (result?.matchingResults) {
        const { invitedCandidates, matchedCandidates, topMatches } = result.matchingResults;
        
        if (invitedCandidates > 0) {
          notification.success({
            message: 'Job Posted & Candidates Invited!',
            description: (
              <div>
                <p><strong>{invitedCandidates}</strong> matching candidates from your pool have been invited to apply for this job.</p>
                <p style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>They will see the invitation in their dashboard.</p>
                {topMatches && topMatches.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <p style={{ marginBottom: 4, fontWeight: 'bold' }}>Top Matches:</p>
                    {topMatches.slice(0, 3).map((match: any, idx: number) => (
                      <div key={idx} style={{ fontSize: '12px' }}>
                        • {match.name} ({match.matchScore}% match)
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ),
            duration: 8,
            placement: 'topRight',
          });
        } else if (matchedCandidates === 0) {
          notification.info({
            message: 'Job Posted Successfully',
            description: 'No candidates in your pool match this job criteria yet. Candidates will be invited as they are added to your pool.',
            duration: 5,
          });
        }
      } else {
        message.success("Job added!");
      }
      
      // Fetch and sort the full list
      const response = await axiosInstant.get('/jobs/recruiter/my-jobs');
      const sortedJobs = response.data.sort(
        (a: IJob, b: IJob) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setJobs(sortedJobs);
    } catch (error: any) {
      message.error(error?.message || "An error occurred");
    } finally {
      setIsSubmittingJob(false);
    }
  };

  const handleInvite = (job: IJob) => {
    setSelectedJob(job);
    setInviteModalVisible(true);
    setInviteLink(null);
    inviteForm.resetFields();
  };

  const handleSendInviteLocal = async () => {
    try {
      const values = await inviteForm.validateFields();
      await handleSendInvite(selectedJob, values);
      notification.success({
        message: "Invitation Sent",
        description: "The invitation has been sent to the candidate successfully.",
        placement: "topRight",
      });
      setInviteModalVisible(false);
    } catch (error) {
      notification.error({
        message: "Failed to Send Invitation",
        description: "An error occurred while sending the invitation.",
        placement: "topRight",
      });
    }
  };

  const fetchInviteStats = async (jobId: string) => {
    try {
      const res = await axiosInstant.get(`/jobs/${jobId}/invite-stats`);
      setInviteStats(res.data);
    } catch {
      setInviteStats(null);
    }
  };

  const handleMoreInfo = (job: IJob) => {
    setDrawerJob(job);
    setDrawerVisible(true);
    fetchInviteStats(job._id);
  };

  const fetchInvitesForJob = async (jobId: string) => {
    const res = await axiosInstant.get(endpoints.listInvitesByJob(jobId));
    const invites = res.data;
    setInvites(invites);
    // Set badge if any invite is unread by recruiter
    setHasInviteUpdates(invites.some((invite: any) => invite.readByRecruiter === false));
    return invites;
  };

  const handleViewInvites = async (job: IJob) => {
    setInvitesJob(job);
    try {
      // Mark as read
      await axiosInstant.post(`/invites/mark-read/${job._id}`);
      // Fetch invites
      const data = await fetchInvitesForJob(job._id);
      setInvites(data);
      setHasInviteUpdates(false);
      // Fetch stats for this job
      const statsRes = await axiosInstant.get(`/jobs/${job._id}/invite-stats`);
      setInvitesStats(statsRes.data);
      setInvitesVisible(true);
    } catch (error) {
      message.error("Failed to fetch invites");
      setInvitesStats(null);
    }
  };

  const handleConfigureScreening = (job: IJob) => {
    setSelectedJobForScreening(job);
    setScreeningCriteriaVisible(true);
  };

  const handleScreeningCriteriaClose = () => {
    setScreeningCriteriaVisible(false);
    setSelectedJobForScreening(null);
  };

  const handleScreeningCriteriaSave = (criteria: any) => {
    message.success('Screening criteria updated successfully!');
    // Optionally refresh jobs data or update local state
  };

  const hasInviteUpdatesForJob = (jobId: string) => invites.some((invite: any) => invite._id === jobId && invite.readByRecruiter === false);
  const hasUnviewedApplicationsForJob = (jobId: string) => unviewedApplicationsByJob[jobId] || false;
  const mappedApplications = invitations.map(invite => ({
    ...invite,
    companyName: invite.companyName || '',
    position: invite.positionTitle || '',
  }));

      const baseColumns = JobsColumns(handleInvite, handleMoreInfo, handleViewInvites, hasInviteUpdatesForJob, hasUnviewedApplicationsForJob, jobChatCounts, handleConfigureScreening) || [];
  const companyIndex = baseColumns.findIndex(col => col.key === 'companyName');
  const jobsColumns =
    companyIndex !== -1
      ? [
          ...baseColumns.slice(0, companyIndex + 1),
          {
            title: 'Total Applicants',
            key: 'totalApplicants',
            align: 'center' as const,
            render: (_: any, record: IJob) => jobApplicationsCount[record._id] ?? '-',
          },
          ...baseColumns.slice(companyIndex + 1),
        ]
      : baseColumns;

  return (
    <>
        <div style={{ padding: 24 }}>
       {currentPage > 1 && (
        <Button
          type="default"
          style={{
            marginBottom: 16,
            marginLeft: 8,
            background: '#fff',
            color: '#222',
            border: '1px solid #d9d9d9',
            fontWeight: 500
          }}
          onClick={() => navigate(-1)}
        >
          ← Back
        </Button>
      )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingRight: 24,
          paddingTop: 24,
        }}
      >
        <h2 style={{ margin: 0, paddingLeft: 24, fontSize: '36px' }}>Jobs</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            type="default"
            onClick={() => navigate('/job-board')}
            style={{
              background: '#fff',
              color: '#6841eb',
              border: '1px solid #6841eb',
              fontWeight: 500,
              fontSize: '16px',
              height: '40px'
            }}
          >
            View Public Job Board
          </Button>
          <Button
            type="primary"
            onClick={() => setAddModalVisible(true)}
            style={{
              fontSize: '16px',
              height: '40px'
            }}
          >
            Add Job
          </Button>
        </div>
      </div>
      <div style={{ marginTop: 32 }}>
        <Table
          dataSource={jobs}
          columns={jobsColumns.map(col => ({
            ...col,
            title: typeof col.title === 'string' ? <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{col.title}</span> : col.title,
            render: col.render ? (text: string | number, record: IJob, index: number) => {
              const rendered = col.render!(text, record, index);
              if (React.isValidElement(rendered)) {
                return React.cloneElement(rendered as React.ReactElement<any>, {
                  style: { ...(rendered as React.ReactElement<any>).props.style, fontSize: '16px' }
                });
              }
              return <span style={{ fontSize: '16px' }}>{String(rendered)}</span>;
            } : (text: string | number) => <span style={{ fontSize: '16px' }}>{text}</span>
          }))}
          rowKey="_id"
          pagination={{
            current: currentPage,
            pageSize: 10,
            onChange: (page) => {
              setCurrentPage(page);
              navigate(`?page=${page}`);
            },
            showSizeChanger: false,
            style: { fontSize: '16px' }
          }}
          components={{
            body: {
              cell: (props: any) => (
                <td {...props} style={{ ...props.style, fontSize: '16px' }} />
              ),
            },
          }}
        />
      </div>
      <Modal
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>Add New Job</span>}
        visible={addModalVisible}
        onOk={handleAddJob}
        confirmLoading={isSubmittingJob}
        onCancel={() => setAddModalVisible(false)}
        okButtonProps={{ disabled: isSubmittingJob }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="companyName"
            label={<span style={{ fontSize: '16px' }}>Company Name</span>}
            rules={[{ required: true, message: "Please enter company name" }]}
          >
            <Input style={{ fontSize: '15px' }} />
          </Form.Item>
          <Form.Item
            name="companyDescription"
            label={<span style={{ fontSize: '16px' }}>About the Company (optional)</span>}
            tooltip="A sentence or two about what your company actually does, its industry, or culture. Used to write a genuine company overview in the generated job description instead of generic filler."
          >
            <Input.TextArea style={{ fontSize: '15px' }} rows={2} placeholder="e.g. Founding Dev builds custom software for early-stage startups, pairing engineers directly with founders." />
          </Form.Item>
          <Form.Item
            name="positionTitle"
            label={<span style={{ fontSize: '16px' }}>Position Title</span>}
            rules={[{ required: true, message: "Please enter position title" }]}
          >
            <Input style={{ fontSize: '15px' }} />
          </Form.Item>
          <Form.Item
            name="workType"
            label={<span style={{ fontSize: '16px' }}>Work Type</span>}
            rules={[{ required: true, message: "Please select work type" }]}
          >
            <Select style={{ fontSize: '15px' }}>
              <Option value="Onsite">Onsite</Option>
              <Option value="Hybrid">Hybrid</Option>
              <Option value="Remote">Remote</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="location"
            label={<span style={{ fontSize: '16px' }}>Location</span>}
            rules={[{ required: true, message: "Please enter location" }]}
          >
            <Input style={{ fontSize: '15px' }} />
          </Form.Item>
         
          <Form.Item
            name="requiredSkills"
            label={<span style={{ fontSize: '16px' }}>Required Skills</span>}
            rules={[{ required: true, message: "Please enter required skills" }]}
          >
            <Select mode="tags" style={{ width: '100%', fontSize: '15px' }} placeholder="e.g. JavaScript, React, Teamwork" />
          </Form.Item>
          <Form.Item
            name="salary"
            label={<span style={{ fontSize: '16px' }}>Salary Range</span>}
            rules={[{ required: true, message: "Please enter salary range" }]}
          >
            <Input style={{ fontSize: '15px' }} placeholder="e.g. $60,000 - $80,000" />
          </Form.Item>
          <Form.Item
            name="benefits"
            label={<span style={{ fontSize: '16px' }}>Benefits</span>}
            rules={[{ required: true, message: "Please enter benefits" }]}
          >
            <Input.TextArea style={{ fontSize: '15px' }} rows={2} placeholder="e.g. Health insurance, Remote work, Annual bonus" />
          </Form.Item>
          <Form.Item
            name="jobDescription"
            label={<span style={{ fontSize: '16px' }}>Job Description</span>}
            rules={[{ required: true, message: "Please enter job description" }]}
          >
            <Input.TextArea style={{ fontSize: '15px' }} rows={4} />
          </Form.Item>
          <Button
            type="dashed"
            loading={genLoading}
            style={{ marginBottom: 16 }}
            onClick={async () => {
              try {
                const values = form.getFieldsValue([
                  "positionTitle",
                  "companyName",
                  "companyDescription",
                  "salary",
                  "location",
                  "workType",
                  "requiredSkills",
                  "benefits"
                ]);
                if (!values.positionTitle || !values.companyName || !values.salary) {
                  message.error("Please fill in Position Title, Company Name, and Salary first.");
                  return;
                }
                setGenLoading(true);
                const res = await axiosInstant.post("/jobs/generate-job-description", {
                  position: values.positionTitle,
                  company: values.companyName,
                  companyDescription: values.companyDescription,
                  salary: values.salary,
                  location: values.location,
                  workType: values.workType,
                  requiredSkills: values.requiredSkills,
                  benefits: values.benefits,
                });
                form.setFieldsValue({ jobDescription: res.data.description });
                message.success("Job description generated!");
              } catch (err) {
                message.error("Failed to generate job description.");
              } finally {
                setGenLoading(false);
              }
            }}
          >
            Generate Job Description with AI
          </Button>
         
          <Form.Item
            name="predefinedQuestions"
            label="Screening Questions"
            rules={[{ required: false }]}
          >
            <Checkbox.Group>
              <Space direction="vertical">
                {PREDEFINED_SCREENING_QUESTIONS.map((q) => (
                  <Checkbox key={q} value={q}>
                    {q}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
          <Form.List name="customQuestions">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <div key={field.key} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <Form.Item
                      {...field}
                      name={[field.name]}
                      rules={[{ required: true, message: "Enter custom question" }]}
                      style={{ flex: 1, marginRight: 8 }}
                    >
                      <Input placeholder="Enter custom question" />
                    </Form.Item>
                    <Button onClick={() => remove(field.name)} danger>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  Add Custom Question
                </Button>
              </>
            )}
          </Form.List>
          <Form.List name="managers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      rules={[{ required: true, message: 'Manager name required' }]}
                    >
                      <Input placeholder="Manager Name" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'email']}
                      rules={[
                        { required: true, message: 'Manager email required' },
                        { type: 'email', message: 'Invalid email' }
                      ]}
                    >
                      <Input placeholder="Manager Email" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                    Add Manager
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
      <Modal
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>{`Invite Candidate for ${selectedJob?.positionTitle || ""}`}</span>}
        visible={inviteModalVisible}
        onOk={handleSendInviteLocal}
        onCancel={() => setInviteModalVisible(false)}
        okText="Send Invite"
      >
        {(selectedJob?.predefinedQuestions?.length || 0) + (selectedJob?.customQuestions?.length || 0) > 0 && (
          <div style={{ marginBottom: 16, fontSize: '15px' }}>
            <b>Screening Questions for this job:</b>
            <ul style={{ marginTop: 8 }}>
              {selectedJob?.predefinedQuestions?.map((q, idx) => (
                <li key={`pre-${idx}`}>{q}</li>
              ))}
              {selectedJob?.customQuestions?.map((q, idx) => (
                <li key={`custom-${idx}`}>{q}</li>
              ))}
            </ul>
          </div>
        )}
        <Form form={inviteForm} layout="vertical">
          <Form.Item
            name="candidateName"
            label={<span style={{ fontSize: '16px' }}>Candidate Name</span>}
            rules={[{ required: true, message: "Please enter candidate name" }]}
          >
            <Input style={{ fontSize: '15px' }} />
          </Form.Item>
          <Form.Item
            name="candidateEmail"
            label={<span style={{ fontSize: '16px' }}>Candidate Email</span>}
            rules={[
              { required: true, message: "Please enter candidate email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input style={{ fontSize: '15px' }} />
          </Form.Item>
        </Form>
        {inviteLink && (
          <div style={{ marginTop: 16 }}>
            <b>Invite Link:</b>
            <Input value={inviteLink} readOnly style={{ marginTop: 8 }} />
            {/* Add a copy-to-clipboard button if you want */}
          </div>
        )}
      </Modal>
      <Drawer
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>{drawerJob?.positionTitle}</span>}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={400}
      >
        {drawerJob && (
          <div style={{ fontSize: '15px' }}>
            <p><b>Salary:</b> {drawerJob.salary}</p>
            <b>Job Description:</b>
            <div>
              {renderJobDescription(drawerJob?.jobDescription)}
            </div>
            {drawerJob.predefinedQuestions && drawerJob.predefinedQuestions.length > 0 && (
              <>
                <b>Pre-screening Questions:</b>
                <ul>
                  {drawerJob.predefinedQuestions.map((q, idx) => <li key={idx}>{q}</li>)}
                </ul>
              </>
            )}
            {drawerJob.customQuestions && drawerJob.customQuestions.length > 0 && (
              <>
                <b>Custom Questions:</b>
                <ul>
                  {drawerJob.customQuestions.map((q, idx) => <li key={idx}>{q}</li>)}
                </ul>
              </>
            )}
          </div>
        )}
      </Drawer>
      <Drawer
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>{`Invites for ${invitesJob?.positionTitle || ""}`}</span>}
        open={invitesVisible}
        onClose={() => setInvitesVisible(false)}
        width={500}
      >
        {invitesStats && (
          <Row gutter={16} style={{ marginBottom: 16, textAlign: 'center' }}>
            <Col span={8}>
              <Card bodyStyle={{ textAlign: 'center' }}><Statistic title={<span style={{textAlign:'center',display:'block', fontSize: '16px'}}>Invites Sent</span>} value={invitesStats.sent} /></Card>
            </Col>
            <Col span={8}>
              <Card bodyStyle={{ textAlign: 'center' }}><Statistic title={<span style={{textAlign:'center',display:'block', fontSize: '16px'}}>Accepted</span>} value={invitesStats.accepted} valueStyle={{ color: '#3f8600' }} /></Card>
            </Col>
            <Col span={8}>
              <Card bodyStyle={{ textAlign: 'center' }}><Statistic title={<span style={{textAlign:'center',display:'block', fontSize: '16px'}}>Declined</span>} value={invitesStats.declined} valueStyle={{ color: '#cf1322' }} /></Card>
            </Col>
          </Row>
        )}
        <Table
          dataSource={invites}
          rowKey="_id"
          bordered
          columns={[
            { 
              title: <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Candidate Name</span>, 
              dataIndex: "candidateName", 
              key: "candidateName", 
              width: 150, 
              ellipsis: true,
              render: (text: string) => <span style={{ fontSize: '15px' }}>{text}</span>
            },
            { 
              title: <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Candidate Email</span>, 
              dataIndex: "candidateEmail", 
              key: "candidateEmail", 
              width: 200, 
              ellipsis: true,
              render: (text: string) => <span style={{ fontSize: '15px' }}>{text}</span>
            },
            {
              title: <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Status</span>,
              dataIndex: "status",
              key: "status",
              width: 120,
              render: (status: string) => {
                let color = "default";
                if (status === "accepted") color = "green";
                else if (status === "declined" || status === "rejected") color = "red";
                else if (status === "pending") color = "gold";
                return <Tag color={color} style={{ textTransform: "capitalize", fontSize: '15px' }}>{status}</Tag>;
              }
            },
            {
              title: <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Sent At</span>,
              dataIndex: "createdAt",
              key: "createdAt",
              width: 200,
              render: (val: string) => <span style={{ fontSize: '15px' }}>{new Date(val).toLocaleString()}</span>,
            },
          ]}
          pagination={false}
          scroll={{ x: true }}
        />
      </Drawer>

      <ScreeningCriteriaModal
        visible={screeningCriteriaVisible}
        jobId={selectedJobForScreening?._id || null}
        onClose={handleScreeningCriteriaClose}
        onSave={handleScreeningCriteriaSave}
      />
    
    </>
  );
};

export default JobsPage;
